// The admin assistant's client side: what the API sends (types), the answer stream, and the page
// context every message carries (the route and the ids on screen, never the page itself).
import { useEffect, useRef, useSyncExternalStore } from 'react';
import { ApiError } from './api';

/* ------------------------------------------------------------------ Panel state */

// Whether the panel is open and which conversation it shows, kept for the tab's session so a
// reload or a page change doesn't lose the place.
const remember = (k: string, v: string | null) => {
  try {
    if (v === null) sessionStorage.removeItem(k);
    else sessionStorage.setItem(k, v);
  } catch {
    // Storage blocked: the panel just starts closed next time.
  }
};
const recall = (k: string) => {
  try {
    return sessionStorage.getItem(k);
  } catch {
    return null;
  }
};
let panel = { open: recall('gp-assistant-open') === '1', thread: recall('gp-assistant-thread') };
const panelSubs = new Set<() => void>();
export function setPanel(next: Partial<typeof panel>) {
  panel = { ...panel, ...next };
  remember('gp-assistant-open', panel.open ? '1' : null);
  remember('gp-assistant-thread', panel.thread);
  panelSubs.forEach((f) => f());
}
export function usePanel() {
  return useSyncExternalStore(
    (f) => (panelSubs.add(f), () => panelSubs.delete(f)),
    () => panel,
  );
}
/** Whether the assistant panel is open (drawers and the page make room for it on wide screens). */
export const useAssistantOpen = () => usePanel().open;

export interface ActionView {
  id: string;
  tool: string;
  label: string;
  risk: 'read' | 'safe' | 'critical';
  status: 'pending' | 'approved' | 'executed' | 'failed' | 'rejected' | 'expired';
  method: string;
  path: string;
  body: unknown;
  /** Claude's own words for the change: its claim, shown after what the server found. */
  summary: string | null;
  /** What the change does, written by the server from the database when Claude asked. */
  effect: string | null;
  /** What to check, in full: amounts, and any text users will read. */
  facts: { label: string; value: string; tone?: 'money' | 'text' | 'warn' }[];
  /** Can be approved with others in one go (never money, settings, staff or text users read). */
  bulk: boolean;
  note: string | null;
  httpStatus: number | null;
  expiresAt: number | null;
  createdAt: number;
  decidedAt: number | null;
}

export interface FileRef {
  id: string;
  name: string;
  kind: 'image' | 'text';
}

export type Item =
  | {
      kind: 'user';
      id: string;
      at: number;
      text: string;
      files: FileRef[];
      page: PageContext | null;
    }
  | { kind: 'assistant'; id: string; at: number; text: string }
  | { kind: 'action'; id: string; at: number; action: ActionView };

export interface ThreadView {
  thread: { id: string; title: string; createdAt: number; updatedAt: number; busy: boolean };
  items: Item[];
  waiting: number;
}

export interface AssistantStatus {
  enabled: boolean;
  configured: boolean;
  model: string;
  limits: {
    imageBytes: number;
    imageSide: number;
    textChars: number;
    filesPerMessage: number;
    messageChars: number;
  };
  threads: { id: string; title: string; updatedAt: number; waiting: number }[];
}

/** One event of an answer stream (see apps/api/src/routes/admin/assistant.ts). */
export type StreamEvent =
  | { event: 'text'; data: { delta: string } }
  | {
      event: 'tool';
      data: {
        id: string;
        tool: string;
        label: string;
        path: string;
        status: 'running' | 'executed' | 'failed';
        note?: string | null;
      };
    }
  | { event: 'approval'; data: { action: ActionView } }
  | { event: 'retry'; data: Record<string, never> }
  | { event: 'error'; data: { message: string } }
  | { event: 'done'; data: { state: 'idle' | 'waiting' | 'cancelled' | 'error' } };

/** POST and read the answer as it streams; resolves when the stream ends. */
export async function streamAnswer(
  path: string,
  body: unknown,
  signal: AbortSignal,
  on: (e: StreamEvent) => void,
): Promise<void> {
  const res = await fetch(`/api/admin${path}`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json', accept: 'text/event-stream' },
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok || !res.body) {
    const data = (await res.json().catch(() => ({}))) as { error?: unknown };
    throw new ApiError(
      res.status,
      typeof data.error === 'string' ? data.error : `Request failed (${res.status})`,
    );
  }
  const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
  let buffer = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += value;
    let cut: number;
    while ((cut = buffer.indexOf('\n\n')) >= 0) {
      const chunk = buffer.slice(0, cut);
      buffer = buffer.slice(cut + 2);
      const event = /^event: (.*)$/m.exec(chunk)?.[1];
      const data = /^data: (.*)$/m.exec(chunk)?.[1];
      if (event && data) on({ event, data: JSON.parse(data) } as StreamEvent);
    }
  }
}

/* ------------------------------------------------------------------ Page context */

/** The ids a page can put in front of the assistant (the API accepts these and no others). */
export type PageIds = Partial<
  Record<
    | 'userId'
    | 'ticketId'
    | 'withdrawalId'
    | 'providerId'
    | 'flagId'
    | 'holdId'
    | 'legacyUserId'
    | 'reviewId'
    | 'postbackId'
    | 'giftcardId',
    string | null | undefined
  >
>;
export interface PageContext {
  path: string;
  ids: Record<string, string>;
}

const ID = /^[A-Za-z0-9_.:-]{1,100}$/;
const contributions = new Map<symbol, PageIds>();

/**
 * Tell the assistant which ids this page (or open drawer) shows, while it's mounted:
 * `useAssistantContext({ userId: ticket.userId })`. Routes with an id in the path (/users/:id,
 * /support/:id) and /withdrawals?id= are read without it.
 */
export function useAssistantContext(ids: PageIds) {
  const key = useRef(Symbol('page'));
  const flat = JSON.stringify(ids);
  useEffect(() => {
    const k = key.current;
    contributions.set(k, JSON.parse(flat) as PageIds);
    return () => void contributions.delete(k);
  }, [flat]);
}

/** Where the staff member is now, for the next message. */
export function currentPage(): PageContext {
  const { pathname, search } = window.location;
  const ids: Record<string, string> = {};
  const add = (k: string, v: string | null | undefined) => {
    if (v && ID.test(v)) ids[k] = v;
  };
  const [, section, id] = pathname.split('/');
  if (section === 'users') add('userId', id);
  if (section === 'support') add('ticketId', id);
  if (section === 'withdrawals') add('withdrawalId', new URLSearchParams(search).get('id'));
  for (const c of contributions.values()) for (const [k, v] of Object.entries(c)) add(k, v);
  return { path: pathname.replace(/[^\w\-./:~,]/g, '').slice(0, 300) || '/', ids };
}
